import { useCallback, useEffect, useRef, useState } from "react";

interface Options {
  edge: "left" | "right";
  min: number;
  max: number;
  initial: number;
  collapseBelow?: number; // if drag goes below this, the consumer can snap-collapse
  onCommit: (width: number) => void; // called on drag end with the final width
  onCollapse?: () => void; // called when drag ends below `collapseBelow`
}

/**
 * Headless drag-to-resize handle. Returns the live width and a set of
 * pointer handlers to spread onto the visual handle element.
 *
 * Resize direction:
 *   • edge="right" — handle is on the pane's right edge, drag right = wider
 *   • edge="left"  — handle is on the pane's left edge,  drag left  = wider
 *
 * The pane is rendered at the returned `width`. We deliberately don't try to
 * also support keyboard-driven resize — the layout preset menu covers that
 * use case.
 */
export function useResizablePane({
  edge,
  min,
  max,
  initial,
  collapseBelow,
  onCommit,
  onCollapse,
}: Options) {
  const [width, setWidth] = useState(initial);
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(initial);

  useEffect(() => {
    setWidth(initial);
  }, [initial]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      setDragging(true);
    },
    [width],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      const delta = e.clientX - startXRef.current;
      const next =
        edge === "right"
          ? startWidthRef.current + delta
          : startWidthRef.current - delta;
      const clamped = Math.max(
        collapseBelow ?? min,
        Math.min(max, Math.round(next)),
      );
      setWidth(clamped);
    },
    [dragging, edge, max, min, collapseBelow],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setDragging(false);
      // If the final drag position was below the collapse threshold, fire the
      // collapse callback instead of committing the (clamped) min width.
      const finalDelta = e.clientX - startXRef.current;
      const finalRaw =
        edge === "right"
          ? startWidthRef.current + finalDelta
          : startWidthRef.current - finalDelta;
      if (collapseBelow !== undefined && finalRaw < collapseBelow && onCollapse) {
        setWidth(startWidthRef.current);
        onCollapse();
      } else {
        const clamped = Math.max(min, Math.min(max, Math.round(finalRaw)));
        setWidth(clamped);
        onCommit(clamped);
      }
    },
    [dragging, edge, max, min, collapseBelow, onCommit, onCollapse],
  );

  return {
    width,
    dragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      role: "separator" as const,
      "aria-orientation": "vertical" as const,
    },
  };
}
